// Shared time/date helpers for consistent formatting across apps.
export const DB_DATE_FORMAT = "YYYY-MM-DD" as const;
export const DB_TIME_FORMAT = "HH:mm" as const;
export const DB_DATETIME_FORMAT =
  `${DB_DATE_FORMAT} ${DB_TIME_FORMAT}` as const;

export const MIN_TIME_BLOCK_MINUTES = 5 as const;
const MINUTES_PER_DAY = 24 * 60;

const DB_TIME_REGEX = /^(\d{1,2}):(\d{2})$/;

export const DEFAULT_DATETIME_FORMAT = "MM/DD/YYYY hh:mm A" as const;
export const DEFAULT_DATE_FORMAT = "MM/DD" as const;
export const DEFAULT_TIME_FORMAT = "h:mm A" as const;
export const DEFAULT_TIMEZONE = "America/Los_Angeles" as const;
export const DISPLAY_DATE_FORMAT = "MMM D, YYYY" as const;

export const parseDbTimeToMinutes = (value: unknown): number | null => {
  if (typeof value !== "string") return null;
  const match = value.match(DB_TIME_REGEX);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours === 24 && minutes === 0) return MINUTES_PER_DAY;
  if (hours < 0 || hours > 23) return null;
  if (minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
};

export const minutesToDbTime = (value: number): string => {
  if (!Number.isFinite(value)) return "00:00";
  const normalized = Math.round(value);
  const wrapped =
    ((normalized % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(wrapped / 60)
    .toString()
    .padStart(2, "0");
  const minutes = (wrapped % 60).toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

export const expandTimeRangeToSlots = (
  open: string,
  close: string,
  blockMinutes: number = MIN_TIME_BLOCK_MINUTES,
): string[] => {
  const start = parseDbTimeToMinutes(open);
  const end = parseDbTimeToMinutes(close);
  if (start == null || end == null) return [];
  const interval = Math.max(1, Math.round(blockMinutes));
  if (!Number.isFinite(interval) || interval <= 0) return [];
  if (end < start) return [];
  // Close time is exclusive — the last bookable slot must START before close.
  const total = Math.floor((end - start) / interval);
  if (total <= 0) return [];
  const slots: string[] = [];
  for (let index = 0; index < total; index += 1) {
    slots.push(minutesToDbTime(start + index * interval));
  }
  return slots;
};

export const serviceDurationToSlots = (
  durationMinutes: number,
  startTime: string,
  blockMinutes: number = MIN_TIME_BLOCK_MINUTES,
): string[] => {
  const start = parseDbTimeToMinutes(startTime);
  if (start == null) return [];
  const interval = Math.max(1, Math.round(blockMinutes));
  if (!Number.isFinite(interval) || interval <= 0) return [];
  const total = Math.floor(durationMinutes / interval);
  if (total <= 0) return [];
  const slots: string[] = [];
  for (let index = 0; index < total; index += 1) {
    slots.push(minutesToDbTime(start + index * interval));
  }
  return slots;
};

export const formatCompactTime = (
  time: string,
  options?: {
    uppercase?: boolean;
    alwaysShowMinutes?: boolean;
    use24Hour?: boolean;
  },
): string => {
  const minutes = parseDbTimeToMinutes(time);
  if (minutes == null) return time;

  const hour24 = Math.floor(minutes / 60);
  const minuteValue = minutes % 60;

  if (options?.use24Hour) {
    return `${hour24}:${minuteValue.toString().padStart(2, "0")}`;
  }

  const period = minutes >= 12 * 60 ? "pm" : "am";
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const minutePart =
    minuteValue === 0 && !options?.alwaysShowMinutes
      ? ""
      : `:${minuteValue.toString().padStart(2, "0")}`;
  const suffix = options?.uppercase ? period.toUpperCase() : period;
  return `${hour12}${minutePart} ${suffix}`;
};

/**
 * An instant as "HH:mm" on a timezone's wall clock, for `formatCompactTime`.
 * Returns null for an invalid instant or timezone.
 */
export const clockInZone = (iso: string | Date, timeZone: string): string | null => {
  const date = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(date);
    const hour = parts.find((p) => p.type === "hour")?.value;
    const minute = parts.find((p) => p.type === "minute")?.value;
    if (!hour || !minute) return null;
    return `${hour === "24" ? "00" : hour}:${minute}`;
  } catch {
    return null;
  }
};

/** "9:30am" for an instant on the location's clock (12-hour, every surface). */
export const formatClockInZone = (iso: string | Date, timeZone: string): string => {
  const clock = clockInZone(iso, timeZone);
  return clock ? formatCompactTime(clock, { alwaysShowMinutes: true }) : "";
};
